import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import config from '../../config/domains';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Grid,
    LinearProgress,
    Chip,
    IconButton,
    Tooltip,
    TextField,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Alert,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CardActions,
    Tabs,
    Tab
} from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import { fetchProjects } from 'store/slices/project';
import EmbeddedJupyter from '../lambda-functions/components/EmbeddedJupyter';
import NotebookManager from './components/NotebookManager';
import KernelManagement from './components/KernelManagement';
import ServerMonitoring from './components/ServerMonitoring';
import JupyterSettings from './components/JupyterSettings';
import ProjectEnvironmentSimple from './ProjectEnvironmentSimple';
import jupyterhubTokenService from 'services/jupyterhubTokenService';
import jupyterhubWorkspaceService from 'services/jupyterhubWorkspaceService';

// icons
import {
    IconBrandPython,
    IconRefresh,
    IconSearch,
    IconSettings,
    IconServer,
    IconCloud,
    IconCopy,
    IconExternalLink,
    IconCode,
    IconNotebook,
    IconCpu,
    IconFolder,
    IconActivity,
    IconX
} from '@tabler/icons-react';

// assets
import JupyterHubLogo from '../../assets/icons/Jupyter_logo.svg.png';

// ==============================|| JUPYTER DASHBOARD ||============================== //

const JupyterDashboard = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const location = useLocation();
    const { projects } = useSelector((state) => state.project);

    const [loading, setLoading] = useState(false);
    const [showJupyterViewer, setShowJupyterViewer] = useState(false);
    const [showConnectionDialog, setShowConnectionDialog] = useState(false);
    const [showEnvironmentDialog, setShowEnvironmentDialog] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [filterType, setFilterType] = useState('all');
    const [tabValue, setTabValue] = useState(0);
    const [connectionInfo, setConnectionInfo] = useState({
        url: '',
        username: '',
        password: '',
        token: ''
    });
    const [jupyterMetrics, setJupyterMetrics] = useState({
        activeNotebooks: 0,
        totalKernels: 0,
        totalProjects: 0,
        runningServers: 0
    });

    // Get user token from localStorage
    const userToken = localStorage.getItem('user_token') || '';

    // Map routes to tab indices
    const routeToTab = {
        '/jupyter/dashboard': 0,
        '/jupyter/notebooks': 1,
        '/jupyter/kernels': 2,
        '/jupyter/monitoring': 3,
        '/jupyter/settings': 4,
        '/jupyter/environments': 5
    };

    useEffect(() => {
        // Set tab based on current route
        const currentTab = routeToTab[location.pathname] || 0;
        setTabValue(currentTab);

        // Clear JupyterHub session when accessing dashboard
        const clearSessionAndLoad = async () => {
            console.log('Clearing JupyterHub session on dashboard access');
            await jupyterhubWorkspaceService.forceLogoutAll();
            loadProjects();
            loadJupyterMetrics();
        };

        clearSessionAndLoad();
    }, [location.pathname]);

    const loadProjects = async () => {
        setLoading(true);
        try {
            await dispatch(fetchProjects());
        } catch (error) {
            console.error('Error loading projects:', error);
        }
        setLoading(false);
    };

    const loadJupyterMetrics = async () => {
        // Mock data - replace with actual API call
        setJupyterMetrics({
            activeNotebooks: 24,
            totalKernels: 18,
            totalProjects: projects?.length || 0,
            runningServers: 12
        });
    };

    const handleProjectSelect = (project) => {
        setSelectedProject(project);
        setShowJupyterViewer(true);
    };

    const handleBackToProjects = async () => {
        // Clear JupyterHub session before going back
        console.log('Clearing JupyterHub session on back to projects');
        await jupyterhubWorkspaceService.forceLogoutAll();

        setShowJupyterViewer(false);
        setSelectedProject(null);
    };

    const handleShowConnection = (project) => {
        setSelectedProject(project);
        const jupyterUsername = `${userToken}_${project.project_name}`.toLowerCase();

        setConnectionInfo({
            url: `${config.JUPYTER_URL}/hub/user/${jupyterUsername}/lab`,
            username: jupyterUsername,
            password: project.passwd_jupyterhub || '••••••••',
            token: project.jupyterhub_token || 'Not available'
        });
        setShowConnectionDialog(true);
    };

    const handleOpenJupyterHub = async (project) => {
        try {
            await jupyterhubTokenService.openWorkspace(project.project_name);
        } catch (error) {
            console.error('Error opening JupyterHub:', error);
            // Fallback to old method if token service fails
            const jupyterUsername = `${userToken}_${project.project_name}`.toLowerCase();
            const jupyterUrl = `${config.JUPYTER_URL}/hub/user/${jupyterUsername}/lab`;
            window.open(jupyterUrl, '_blank');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        // Show toast notification
    };

    const filteredProjects =
        projects?.filter((project) => {
            const matchesSearch =
                project.project_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                project.description?.toLowerCase().includes(searchTerm.toLowerCase());
            return matchesSearch;
        }) || [];

    const sortedProjects = [...filteredProjects].sort((a, b) => {
        switch (sortBy) {
            case 'name':
                return a.project_name.localeCompare(b.project_name);
            case 'activity':
                return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
            case 'date':
                return new Date(b.created_at) - new Date(a.created_at);
            default:
                return 0;
        }
    });

    // Status cards data
    const statusCards = [
        {
            title: 'Active Notebooks',
            value: jupyterMetrics.activeNotebooks.toLocaleString(),
            icon: <IconNotebook size={24} />,
            color: theme.palette.primary.main,
            description: 'Currently running'
        },
        {
            title: 'Total Kernels',
            value: jupyterMetrics.totalKernels.toLocaleString(),
            icon: <IconCpu size={24} />,
            color: theme.palette.secondary.main,
            description: 'Python, R, Julia kernels'
        },
        {
            title: 'Active Projects',
            value: jupyterMetrics.totalProjects,
            icon: <IconServer size={24} />,
            color: theme.palette.success.main,
            description: 'Projects with Jupyter'
        },
        {
            title: 'Running Servers',
            value: jupyterMetrics.runningServers,
            icon: <IconCloud size={24} />,
            color: theme.palette.warning.main,
            description: 'JupyterHub servers'
        }
    ];

    if (showJupyterViewer && selectedProject) {
        return (
            <MainCard
                title={
                    <Box display="flex" alignItems="center" gap={2}>
                        <IconBrandPython />
                        <Typography variant="h3">Jupyter Lab - {selectedProject.project_name}</Typography>
                        <Chip label="Interactive Computing" size="small" color="primary" />
                    </Box>
                }
                secondary={
                    <Box display="flex" gap={1}>
                        <Button variant="outlined" onClick={handleBackToProjects} size="small">
                            Back to Projects
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<IconExternalLink />}
                            onClick={() => handleOpenJupyterHub(selectedProject)}
                            size="small"
                        >
                            Open in New Tab
                        </Button>
                    </Box>
                }
            >
                <EmbeddedJupyter
                    projectName={selectedProject.project_name}
                    onFunctionDeploy={(functionData) => {
                        console.log('Function deployed from Jupyter:', functionData);
                    }}
                />
            </MainCard>
        );
    }

    return (
        <MainCard
            title={
                <Box display="flex" alignItems="center" gap={2}>
                    <IconBrandPython />
                    <Typography variant="h3">Jupyter Hub Management</Typography>
                    <Chip
                        label="Interactive Computing"
                        size="small"
                        sx={{
                            bgcolor: theme.palette.warning.light,
                            color: theme.palette.warning.dark
                        }}
                    />
                </Box>
            }
            secondary={
                <Box display="flex" gap={1}>
                    <Tooltip title="Refresh">
                        <IconButton onClick={loadProjects} disabled={loading}>
                            <IconRefresh />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<IconSettings />}
                        onClick={() => setShowConnectionDialog(true)}
                        disabled={!selectedProject}
                    >
                        Jupyter Settings
                    </Button>
                </Box>
            }
        >
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {/* Status Cards */}
            <Grid container spacing={gridSpacing} sx={{ mb: 3 }}>
                {statusCards.map((card, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                        <Card
                            sx={{
                                position: 'relative',
                                overflow: 'hidden',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    transition: 'transform 0.3s ease-in-out'
                                }
                            }}
                        >
                            <CardContent>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography color="textSecondary" gutterBottom variant="subtitle2">
                                            {card.title}
                                        </Typography>
                                        <Typography variant="h3" component="div">
                                            {card.value}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {card.description}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            backgroundColor: `${card.color}20`,
                                            borderRadius: '12px',
                                            p: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        {React.cloneElement(card.icon, { style: { color: card.color } })}
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                    <Tab
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <IconFolder size={18} />
                                Projects
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <IconNotebook size={18} />
                                Notebooks
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <IconCpu size={18} />
                                Kernels
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <IconActivity size={18} />
                                Monitoring
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <IconSettings size={18} />
                                Settings
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <IconBrandPython size={18} />
                                Environment Settings
                            </Box>
                        }
                    />
                </Tabs>
            </Box>

            {/* Tab Content */}
            {tabValue === 0 && (
                <>
                    {/* Filter Section */}
                    <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                            placeholder="Search projects..."
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{ flexGrow: 1, maxWidth: 400 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconSearch size={18} />
                                    </InputAdornment>
                                )
                            }}
                        />
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Sort by</InputLabel>
                            <Select value={sortBy} label="Sort by" onChange={(e) => setSortBy(e.target.value)}>
                                <MenuItem value="name">Name</MenuItem>
                                <MenuItem value="activity">Last Activity</MenuItem>
                                <MenuItem value="date">Created Date</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Filter</InputLabel>
                            <Select value={filterType} label="Filter" onChange={(e) => setFilterType(e.target.value)}>
                                <MenuItem value="all">All Projects</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Projects Grid */}
                    <Grid container spacing={gridSpacing}>
                        {sortedProjects.map((project, index) => (
                            <Grid item xs={12} md={6} lg={4} key={index}>
                                <Card
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: theme.shadows[8],
                                            transition: 'all 0.3s ease-in-out'
                                        },
                                        border: '1px solid',
                                        borderColor: theme.palette.divider
                                    }}
                                >
                                    <CardContent>
                                        <Box display="flex" alignItems="center" gap={2} mb={2}>
                                            <Box
                                                sx={{
                                                    backgroundColor: '#D8431520',
                                                    borderRadius: '12px',
                                                    p: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <img src={JupyterHubLogo} alt="Jupyter" style={{ width: 32, height: 32 }} />
                                            </Box>
                                            <Box flexGrow={1}>
                                                <Typography variant="h5" gutterBottom>
                                                    {project.project_name}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    {project.description || 'No description'}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Divider sx={{ my: 2 }} />

                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                            <Box display="flex" alignItems="center">
                                                <Typography variant="body2" color="textSecondary" component="span">
                                                    Status:
                                                </Typography>
                                                <Chip label="Active" size="small" color="success" sx={{ ml: 1 }} />
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="textSecondary">
                                                    Created: {new Date(project.created_at).toLocaleDateString()}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>

                                    <CardActions>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<IconCode />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleProjectSelect(project);
                                            }}
                                            fullWidth
                                            sx={{ mr: 1 }}
                                        >
                                            Open Jupyter Lab
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<IconSettings />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleShowConnection(project);
                                            }}
                                        >
                                            Info
                                        </Button>
                                        <Button
                                            variant="text"
                                            size="small"
                                            startIcon={<IconCpu />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedProject(project);
                                                setShowEnvironmentDialog(true);
                                            }}
                                            title="Manage Python Environments"
                                        >
                                            Env
                                        </Button>
                                        <Button
                                            variant="text"
                                            size="small"
                                            startIcon={<IconExternalLink />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenJupyterHub(project);
                                            }}
                                        >
                                            External
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {sortedProjects.length === 0 && (
                        <Box textAlign="center" py={8}>
                            <IconBrandPython size={64} style={{ opacity: 0.3, marginBottom: 16 }} />
                            <Typography variant="h4" gutterBottom sx={{ opacity: 0.5 }}>
                                No Jupyter projects found
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.5 }}>
                                {searchTerm ? 'Try adjusting your search terms' : 'Create your first project to get started'}
                            </Typography>
                        </Box>
                    )}

                    {/* Connection Info Dialog */}
                    <Dialog open={showConnectionDialog} onClose={() => setShowConnectionDialog(false)} maxWidth="md" fullWidth>
                        <DialogTitle>
                            <Box display="flex" alignItems="center" gap={2}>
                                <IconBrandPython />
                                JupyterHub Connection Info
                                {selectedProject && <Chip label={selectedProject.project_name} size="small" color="primary" />}
                            </Box>
                        </DialogTitle>
                        <DialogContent>
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12}>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        Use these credentials to access your JupyterHub environment.
                                    </Alert>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Jupyter URL"
                                        value={connectionInfo.url}
                                        InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => copyToClipboard(connectionInfo.url)}>
                                                        <IconCopy />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Username"
                                        value={connectionInfo.username}
                                        InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => copyToClipboard(connectionInfo.username)}>
                                                        <IconCopy />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Password"
                                        type="password"
                                        value={connectionInfo.password}
                                        InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => copyToClipboard(connectionInfo.password)}>
                                                        <IconCopy />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Alert severity="warning">
                                        <Typography variant="body2">
                                            <strong>Security Note:</strong> Keep your JupyterHub credentials secure and never share them
                                            publicly.
                                        </Typography>
                                    </Alert>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => window.open(connectionInfo.url, '_blank')}
                                variant="contained"
                                startIcon={<IconExternalLink />}
                            >
                                Open JupyterHub
                            </Button>
                            <Button onClick={() => setShowConnectionDialog(false)}>Close</Button>
                        </DialogActions>
                    </Dialog>

                    {/* Environment Manager Dialog */}
                    <Dialog
                        open={showEnvironmentDialog}
                        onClose={() => setShowEnvironmentDialog(false)}
                        maxWidth="lg"
                        fullWidth
                        PaperProps={{
                            sx: { height: '90vh' }
                        }}
                    >
                        <DialogTitle>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography variant="h6">Project Environment Settings - {selectedProject?.project_name}</Typography>
                                <IconButton onClick={() => setShowEnvironmentDialog(false)}>
                                    <IconX />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <DialogContent sx={{ p: 0 }}>
                            {selectedProject && (
                                <ProjectEnvironmentSimple userToken={userToken} projectName={selectedProject.project_name} />
                            )}
                        </DialogContent>
                    </Dialog>
                </>
            )}

            {/* Notebooks Tab */}
            {tabValue === 1 && <NotebookManager />}

            {/* Kernels Tab */}
            {tabValue === 2 && <KernelManagement />}

            {/* Monitoring Tab */}
            {tabValue === 3 && <ServerMonitoring />}

            {/* Settings Tab */}
            {tabValue === 4 && <JupyterSettings />}

            {/* Environment Settings Tab */}
            {tabValue === 5 && <ProjectEnvironmentSimple userToken={userToken} projectName={selectedProject?.project_name || ''} />}
        </MainCard>
    );
};

export default JupyterDashboard;
